// utils/filters.utils.ts
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
  SelectedFilters,
  STRING_FILTER_KEYS,
} from "../lib/features/filters/filters.types";

const ARRAY_FILTER_KEYS: (keyof SelectedFilters)[] = [
  "gender",
  "category",
  "category_groups",
  "category_sub_groups",
  "color",
  "size",
  "brand",
  "season",
  "seller_badge",
  "rating",
];

const BOOLEAN_FILTER_KEYS: (keyof SelectedFilters)[] = [
  "discounted",
  "new_products",
  "coupon",
  "bulk_sale",
];

export const buildQueryString = (selected: SelectedFilters, excludeKeys: string[] = []): string => {
  const params = new URLSearchParams();

  ARRAY_FILTER_KEYS.forEach((key) => {
    if (excludeKeys.includes(key as string)) return;
    const val = selected[key] as (number | string)[];
    if (val.length > 0) params.set(key as string, val.join(","));
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (selected[key]) params.set(key as string, "true");
  });

  if (selected.price.min !== undefined)
    params.set("min_price", String(selected.price.min));
  if (selected.price.max !== undefined)
    params.set("max_price", String(selected.price.max));

  if (selected.sort && selected.sort !== "default")
    params.set("sort", selected.sort);

  if (selected.special_filters.length)
    params.set("special_filters", selected.special_filters.join(","));

  return params.toString();
};

export const parseFiltersFromSearchParams = (
  searchParams: URLSearchParams,
): Partial<SelectedFilters> => {
  const partial: Partial<SelectedFilters> = {};

  ARRAY_FILTER_KEYS.forEach((key) => {
    const val = searchParams.get(key as string);
    if (!val) return;
    const arr = val.split(",").filter(Boolean);

    if (STRING_FILTER_KEYS.has(key)) {
      (partial[key] as string[]) = arr;
    } else {
      (partial[key] as number[]) = arr.map(Number).filter((n) => !isNaN(n));
    }
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (searchParams.get(key as string) === "true") {
      (partial[key] as boolean) = true;
    }
  });

  const priceMin = searchParams.get("min_price");
  const priceMax = searchParams.get("max_price");
  if (priceMin || priceMax) {
    partial.price = {
      min: priceMin ? Number(priceMin) : undefined,
      max: priceMax ? Number(priceMax) : undefined,
    };
  }

  const sort = searchParams.get("sort");
  if (sort) partial.sort = sort;

  const specialFilters = searchParams.get("special_filters");
  if (specialFilters)
    partial.special_filters = specialFilters.split(",").filter(Boolean);

  return partial;
};

const BOOLEAN_FILTER_KEYS_LIST: (keyof SelectedFilters)[] = [
  "discounted",
  "new_products",
  "coupon",
  "bulk_sale",
];

/**
 * "En az bir filtre seçili kalmalı" kuralının kapsadığı TEK grup üçlüsü:
 * kategoriler, ürün grupları, alt kategoriler.
 *
 * Bu üçü sayfanın BAĞLAMI: slug kontrolünden seçili olarak geliyorlar ve
 * hepsi kaldırılırsa listeleme "hangi kategori" olduğunu kaybediyor. Renk,
 * beden, marka, fiyat vb. daraltma filtreleri; kullanıcı hepsini kaldırınca
 * liste yalnızca genişler, engellenecek bir şey yok. Kural eskiden TÜM
 * filtreleri sayıyordu (countTotalSelectedFilters) ve tek seçili renk
 * kalınca da uyarı çıkıyordu.
 */
export const MINIMUM_GUARDED_KEYS = [
  "category",
  "category_groups",
  "category_sub_groups",
] as const;

export type MinimumGuardedKey = (typeof MINIMUM_GUARDED_KEYS)[number];

export const isMinimumGuardedKey = (key: string): key is MinimumGuardedKey =>
  (MINIMUM_GUARDED_KEYS as readonly string[]).includes(key);

/** Korunan üç grupta toplam kaç seçim var. */
export const countGuardedSelections = (selected: SelectedFilters): number =>
  MINIMUM_GUARDED_KEYS.reduce(
    (total, key) => total + (selected[key] as number[]).length,
    0,
  );

export const countTotalSelectedFilters = (selected: SelectedFilters): number => {
  let count = 0;
  ARRAY_FILTER_KEYS.forEach((key) => {
    const val = selected[key] as (number | string)[];
    count += val.length;
  });
  BOOLEAN_FILTER_KEYS_LIST.forEach((key) => {
    if (selected[key]) count += 1;
  });
  count += selected.special_filters.length;
  if (selected.price.min !== undefined || selected.price.max !== undefined) count += 1;
  return count;
};

export const buildFilterRequestFromSelected = (
  selected: SelectedFilters,
  base: Partial<FilterRequest> = {},
): FilterRequest => {
  const req: FilterRequest = { ...DEFAULT_FILTER_REQUEST, ...base };

  if (selected.gender.length) req.gender = selected.gender;
  if (selected.category.length) req.category = selected.category;
  if (selected.category_groups.length)
    req.category_groups = selected.category_groups;
  if (selected.category_sub_groups.length)
    req.category_sub_groups = selected.category_sub_groups;
  if (selected.product.length) req.product = selected.product;
  if (selected.color.length) req.color = selected.color;
  if (selected.size.length) req.size = selected.size;
  if (selected.brand.length) req.brand = selected.brand;
  if (selected.season.length) req.season = selected.season;
  if (selected.seller_badge.length) req.seller_badge = selected.seller_badge;
  if (selected.rating.length) req.rating = selected.rating;
  if (selected.discounted || selected.special_filters.includes("discounted"))
    req.discounted = true;
  if (selected.new_products || selected.special_filters.includes("new_products"))
    req.new_products = true;
  if (selected.coupon || selected.special_filters.includes("coupon"))
    req.coupon = true;
  if (selected.bulk_sale) req.bulk_sale = true;
  if (selected.special_filters.includes("is_advantageous"))
    req.is_advantageous = true;
  if (selected.special_filters.includes("flash_products"))
    req.flash_products = true;
  if (selected.price.min !== undefined || selected.price.max !== undefined)
    req.price = selected.price;
  if (selected.sort && selected.sort !== "default") req.sort = selected.sort;
  if (selected.special_filters.length)
    req.special_filters = selected.special_filters;

  return req;
};
